import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import { normalizeDateString, normalizeDateStringWithCurrentUtcTime } from '~/utils/date/date';
import { useApiMutation, useApiRequest } from '~/hooks/api';
import { IClinic } from '~/services/api/clinic/types';
import { ADD_CLIENT, CLIENT, VIEW_CLIENT } from '~/ui/constants/paths';
import ClientProfileTab from '~/ui/pages/Clients/LocalAdmin/ClientProfile/constants/clientProfileTabs';
import Loader from '~/ui/components/common/Loader';
import Breadcrumbs from '~/ui/components/common/Breadcrumbs';
import { IInitialValues } from '../Form/initialValues';
import { ADD_CLIENT_LEAVE_DESCRIPTION } from '../Form/constants';
import Form from '../Form';

const AddClient = (): ReactElement => {
  const navigate = useNavigate();

  const clinic = useStoreState(state => state.clinic.current || ({} as IClinic));
  const onGetMyClinic = useStoreActions(actions => actions.clinic.onGetMyClinic);
  const onAddClient = useStoreActions(actions => actions.client.onAddClient);

  const { name: clinicName, id: clinicId } = clinic;

  const { isLoading } = useApiRequest(onGetMyClinic, { initialLoading: true });

  const addClient = ({
    teamId,
    emrMrn,
    isShelter,
    address,
    dateOfBirth,
    ...rest
  }: IInitialValues) =>
    onAddClient({
      requestInfo: { clinicId: String(clinicId), teamId: String(teamId) },
      requestPayload: {
        emrMrn: emrMrn === '' ? null : emrMrn,
        address: isShelter ? null : address,
        ...rest,
        dateOfBirth: normalizeDateString(dateOfBirth),
        admissionDetails: {
          ...rest.admissionDetails,
          admissionDate: normalizeDateStringWithCurrentUtcTime(rest.admissionDetails.admissionDate),
        },
      },
    });

  const { mutate: onSubmit, isLoading: isAdding } = useApiMutation(addClient, {
    notifyMessage: 'Client successfully added',
    onSuccess: ({ id, actTeam }) =>
      navigate(
        VIEW_CLIENT.replace(':clientId', String(id)).replace(':actTeamId', String(actTeam.id)),
        {
          state: { selectedTab: ClientProfileTab.HealthDetails, actTeamId: String(actTeam.id) },
        },
      ),
    throwOnError: false,
  });

  const itemsForBreadcrumbs = [
    { to: CLIENT, title: `${clinicName} Clients` },
    { to: ADD_CLIENT, title: 'Add Client' },
  ];

  if (isLoading) return <Loader />;

  return (
    <div>
      <h2>Add Client</h2>
      <Breadcrumbs itemsForBreadcrumbs={itemsForBreadcrumbs} />
      <Form
        clinicId={String(clinicId)}
        isProcessing={isAdding}
        leavePageDescription={ADD_CLIENT_LEAVE_DESCRIPTION}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default AddClient;
